import React, { createContext, useCallback, useContext, useEffect, useState } from 'react';
import { api, type Profile } from '../api/client';

interface AuthState {
  user: Profile | null;
  loading: boolean;
  refresh: () => Promise<void>;
  ensureGuest: () => Promise<Profile>;
  setUsername: (name: string) => Promise<void>;
}

const Ctx = createContext<AuthState | null>(null);

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const [user, setUser] = useState<Profile | null>(null);
  const [loading, setLoading] = useState(true);

  const refresh = useCallback(async () => {
    try {
      const { user } = await api.me();
      setUser(user);
    } catch {
      setUser(null);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  const ensureGuest = useCallback(async () => {
    if (user) return user;
    const { user: g } = await api.guest();
    setUser(g);
    return g;
  }, [user]);

  const setUsername = useCallback(async (username: string) => {
    const { user: u } = await api.setUsername(username);
    setUser(u);
  }, []);

  return (
    <Ctx.Provider value={{ user, loading, refresh, ensureGuest, setUsername }}>
      {children}
    </Ctx.Provider>
  );
}

export function useAuth(): AuthState {
  const v = useContext(Ctx);
  if (!v) throw new Error('AuthProvider missing');
  return v;
}
